import ColorCardSettingsModalContainer from 'src/components/userManagement/ColorProofGo/ColorCardSettingsModalContainer';
import CpGoSettingsModalContainer from 'src/components/userManagement/ColorProofGo/CpGoSettingsModalContainer';
import { SingleModule } from 'src/components/userManagement/ModuleSelector';
import { CounterLimit, ModuleLicense } from 'src/viewModels/Organization';
import { ProductModule } from '../generated/types';

export const CPGO = { id: '5', name: 'GMG ColorProof GO', cognitoId: 'ColorProofGO' } as const;
export const OPTIMIZE = { id: '9', name: 'GMG Optimize incl. GMG Measure', cognitoId: 'Optimize' } as const;
export const MEASURE = { id: '10', name: 'GMG Measure', cognitoId: 'Measure' } as const;
export const COLORCARD = { id: '20', name: 'GMG ColorCard', cognitoId: 'ColorCard' } as const;

export const ILLUSTRATOR = { id: '30', name: 'GMG ColorPlugin for Adobe® Illustrator®', cognitoId: 'VectorPlugin' } as const;
export const PS_BASIC = { id: '40', name: 'GMG ColorPlugin for Adobe® Photoshop® - Basic Edition', cognitoId: 'PhotoshopPluginBasic' } as const;
export const PS_PACKAGING = { id: '41', name: 'GMG ColorPlugin for Adobe® Photoshop® - Packaging Edition', cognitoId: 'PhotoshopPluginPackaging' } as const;
export const PS_GMG_INTERNAL = { id: '42', name: 'GMG ColorPlugin for Adobe® Photoshop® - GMG Edition', cognitoId: 'PhotoshopPluginINTERNAL' } as const;

export const PS_EDITIONS: Array<ModuleId> = [PS_BASIC.id, PS_PACKAGING.id, PS_GMG_INTERNAL.id];
export const MODULEID_TO_EDITION_NAME = new Map([
    ['40', 'Basic Edition'],
    ['41', 'Packaging Edition'],
    ['42', 'GMG Edition'],
]);

const moduleIds = [
    CPGO.id,
    OPTIMIZE.id,
    MEASURE.id,
    COLORCARD.id,
    ILLUSTRATOR.id,
    PS_BASIC.id,
    PS_PACKAGING.id,
    PS_GMG_INTERNAL.id,
] as const;

export const moduleNames = [
    CPGO.name,
    OPTIMIZE.name,
    MEASURE.name,
    COLORCARD.name,
    ILLUSTRATOR.name,
    PS_BASIC.name,
    PS_PACKAGING.name,
    PS_GMG_INTERNAL.name,
] as const;

// NOTE: I removed CMP from the list 'cos we do not use it here and anyhow we are in CMP !
const moduleIdsInCognito = [
    CPGO.cognitoId,
    OPTIMIZE.cognitoId,
    MEASURE.cognitoId,
    COLORCARD.cognitoId,
    ILLUSTRATOR.cognitoId,
    PS_BASIC.cognitoId,
    PS_PACKAGING.cognitoId,
    PS_GMG_INTERNAL.cognitoId,
] as const;

export type ModuleId = typeof moduleIds[number];
export type ModuleName = typeof moduleNames[number];
export type ModuleIdInCognito = typeof moduleIdsInCognito[number];

// TODO: copied from the back-end. The final goal is to have it only here
export const modules: Map<ModuleId, ModuleName> = new Map(
    moduleIds.map(id => { return [id, moduleNames[moduleIds.indexOf(id)]]; }),
);

export const moduleIdToName: Map<ModuleIdInCognito, ModuleName> = new Map(
    moduleIdsInCognito.map(id => { return [id, moduleNames[moduleIdsInCognito.indexOf(id)]]; }),
);

const MODULEIDS_WITH_ADDITIONAL_SETTINGS = new Set<ModuleId>([CPGO.id, COLORCARD.id]);

export const SETTINGS_MODAL_REGISTRY = {
    [CPGO.id]: CpGoSettingsModalContainer,
    [COLORCARD.id]: ColorCardSettingsModalContainer,
} as const;

export type SettingsModuleId = keyof typeof SETTINGS_MODAL_REGISTRY;
export function hasAdditionalSettingsModule(module: SingleModule): module is SingleModule & { id: SettingsModuleId } {
    return MODULEIDS_WITH_ADDITIONAL_SETTINGS.has(module.id);
}

const getCounterLimit = (moduleLicense: ProductModule): CounterLimit => {
    if (!moduleLicense.counters) return { totalNumberInContracts: 0, availableNumber: 0, assignedNumber: 0 };
    const counters = moduleLicense.counters;
    const totalNumberInContracts = counters.totalNumberInContracts || 0;
    const availableNumber = counters.availableNumber || 0;
    const assignedNumber = totalNumberInContracts - availableNumber;
    return { totalNumberInContracts, availableNumber, assignedNumber };
};

export const mapToModuleLicenseViewModel = (source: Array<ProductModule>): Map<ModuleId, ModuleLicense> => {

    const result = new Map(
        source.map(moduleLicense => {
            const moduleId = moduleLicense.module as ModuleId;

            const expirationDateString = moduleLicense.contractStatus.nearestExpiringContract?.expirationDate || undefined;
            const hasExpired = moduleLicense.contractStatus.nearestExpiringContract?.hasExpired || false;
            const nearestExpiringContract = moduleLicense.contractStatus.nearestExpiringContract
                ? {
                    hasExpired,
                    hasAutoRenewal: moduleLicense.contractStatus.nearestExpiringContract?.autoRenewal,
                    expirationDate: expirationDateString,
                }
                : undefined;

            const details: ModuleLicense = {
                name: modules.get(moduleId)! ?? 'Unknown module',
                effectiveNumberOfAvailableSeats: moduleLicense.seats.availableNumber,
                numberOfAssignedSeats: moduleLicense.seats.numberAssignedToUsers,
                totalNumberOfSeatsInContracts: moduleLicense.seats.totalNumberInContracts,
                type: moduleId === COLORCARD.id // currently this is specific for ColorCard
                    ? 'counterBased'
                    : moduleLicense.seats.isUnlimited
                        ? 'unlimited' : 'seatBased',
                contractStatus: {
                    hasContract: moduleLicense.contractStatus.hasContract,
                    nearestExpiringContract,
                },
                hasAdditionalSettings: MODULEIDS_WITH_ADDITIONAL_SETTINGS.has(moduleId),
                counterLimit: moduleLicense.counterLimit,
                counters: getCounterLimit(moduleLicense),
            };
            return [moduleId, details];
        }));

    return result;
};